import React, {useEffect, useRef, useState} from 'react';
import {Col, DatePicker, Form, Input, message, Row} from 'antd';
import {ProForm, ProFormInstance} from '@ant-design/pro-form';
import {FieldProps, WdiCardTitle, wdiCore, WdiInput, WdiModalAppResult} from '@/components/WdiControls';
import {FormPageProps} from '@/components/WdiBus/FormPage';
import {ApplyFooter, ApplyFooterType} from '@/components/WdiBus/ApplyFooter';
import moment from 'moment';
import {BOHQ} from '@/services/mdm/bohq';
import {FileUploadUtility, WdiFileUpload} from '@/components/WdiControls/WdiFileUpload';
import {FormBudgetProject, FormBudgetProjectDetail} from '@/services/sales/bp/formBudgetProject';
import {BudgetProjectDetail} from '@/services/sales/bp/budgetProject';
import {PageType} from '@/services/basic/pagetype';
import {WdiSelect, WdiSelectFormat, WdiTreeSelect} from '@/components/WdiControls/WdiSelect';
import {EC} from '@/services/mdm/ec';
import {Enum} from '@/enums/system';
import {ResponseStatus} from '@/utils/request';
import BPWarningInfoApp from '../../components/warning';
import {initApplyPageContext, ViewEntityProps} from '../../components/data';
import DataStore from '@/components/GridWidget/DataStore';
import {WdiPanel} from '@/components/WdiControls/WdiPanel';
import FormBPDetailsForApplyPanel from './details';
import {Product} from '@/services/mdm/product';
import {WarningInfoAppProps} from '@/components/WdiBus/PanelForWarning';
import {CustSys} from '@/services/mdm/custsys';
import {SoldTo} from '@/services/mdm/soldto';
import {Staff} from '@/services/basic/staff';
import {WdiEditor} from "@/components/WdiControls/WdiEditor";
import {DataOptions} from "@/services/basic/metadata";

const {RangePicker} = DatePicker;
const {TextArea} = Input;

const getPageResult = (
  values: any,
  viewEntity: ViewEntityProps,
  pageType: API.PageType,
  statusId: number,
  dataSource: any[],
) => {
  const result: WdiModalAppResult = {code: ResponseStatus.UNKNOWN};

  const {
    projectDate,
    applyDate,
    projectApproverIds,
    fileInfo,
    limitedChannelIds,
    limitedEcIds,
    limitedProductIds,
    limitedShopLevelIds,
    limitedSoldToCodes,
    limitedCustSysCodes,
    limitedActivitySubjectIds,
    ...entity
  } = values;
  if (FileUploadUtility.isUploading(fileInfo)) {
    result.message = '附件正在上传中!';
    return result;
  }

  if (FileUploadUtility.isError(fileInfo)) {
    result.message = '附件存在错误文件!';
    return result;
  }

  const req: { form: any; formBudgetProject: any; formBudgetProjectDetails: any[] } = {
    form: {
      statusId: statusId,
      pageTypeId: pageType.pageTypeId,
      pageGroupId: pageType.pageGroupId,
      formTypeId: viewEntity.formTypeId,
    },
    formBudgetProject: {},
    formBudgetProjectDetails: [],
  };

  if (viewEntity.budgetProject) {
    req.form.relatedFormId = viewEntity.relatedFormId;
    req.form.relationType = viewEntity.relationType;
    req.form.budgetProjectId = viewEntity.budgetProject.budgetProjectId;
  } else {
    if (viewEntity.formBudgetProject) {
      if (viewEntity.relationType === Enum.FormRelationType.REJECTED) {
        // 退回待修改
        req.form.relatedFormId = viewEntity.relatedFormId;
        req.form.relationType = viewEntity.relationType;
      } else {
        // 从草稿打开
        req.form.formId = viewEntity.formId;
      }
    }
    entity.modifiedReason = '新建';
  }
  if (projectDate && projectDate.length == 2) {
    entity.projectStartDate = moment(projectDate[0]).format('YYYY-MM-DD');
    entity.projectEndDate = moment(projectDate[1]).format('YYYY-MM-DD');
  } else {
    result.message = '无效的项目日期';
    return result;
  }

  if (applyDate && applyDate.length == 2) {
    entity.applyStartDate = moment(applyDate[0]).format('YYYY-MM-DD');
    entity.applyEndDate = moment(applyDate[1]).format('YYYY-MM-DD');
  }

  //entity.projectApproverIds = ApproverUserIds.approverUserItemToValue(projectApproverIds);

  if (limitedChannelIds) {
    entity.limitedChannelIds = WdiSelectFormat.getvalue(limitedChannelIds);
  }

  if (limitedEcIds) {
    entity.limitedEcIds = WdiSelectFormat.getvalue(limitedEcIds);
  }
  if (limitedProductIds) {
    entity.limitedProductIds = WdiSelectFormat.getvalue(limitedProductIds);
  }
  if (limitedShopLevelIds) {
    entity.limitedShopLevelIds = WdiSelectFormat.getvalue(limitedShopLevelIds);
  }
  if (limitedSoldToCodes) {
    entity.limitedSoldToCodes = WdiSelectFormat.getvalue(limitedSoldToCodes);
  }
  if (limitedCustSysCodes) {
    entity.limitedCustSysCodes = WdiSelectFormat.getvalue(limitedCustSysCodes);
  }
  if (projectApproverIds) {
    entity.projectApproverIds = WdiSelectFormat.getvalue(projectApproverIds);
  }
  if (limitedActivitySubjectIds) {
    entity.limitedActivitySubjectIds = WdiSelectFormat.getvalue(limitedActivitySubjectIds);
  }
  const info = FileUploadUtility.serialize(fileInfo);
  entity.fileName = info?.names;
  entity.filePath = info?.urls;
  req.formBudgetProject = entity;
  if (dataSource && dataSource.length) {
    dataSource.forEach((item, itemIndex) => {
      item.rowNumber = itemIndex + 1;
    });
  } else {
    result.message = '请填写分配明细!';
    return result;
  }

  req.formBudgetProjectDetails = dataSource;
  result.data = req;
  result.code = ResponseStatus.SUCCESS;
  return result;
};

const FormBudgetProjectApplyApp: React.FC<FormPageProps> = (props) => {
  const formRef = useRef<ProFormInstance>();

  const [entity, setEntity] = useState<ViewEntityProps>();
  const [pageType, setPageType] = useState<API.PageType>();
  const [boHQ, setBOHQ] = useState<API.BOHQ>();
  const [hiddenButton, setHiddenButtons] = useState<number[]>([]);
  const [warningSettings, setWarningSettings] = useState<WarningInfoAppProps>({visible: false});
  const [dataSource, setDataSource] = useState<any[]>([]);

  const [expandKeysEC, setExpandKeysEC] = useState<string[]>();
  const [expandKeysProduct, setExpandKeysProduct] = useState<string[]>();
  const [custSysCodes, setCustSysCodes] = useState<string>();
  const [editorRef, setEditorRef] = useState<any>();

  const onDetailsChange = (data: any) => {
    setDataSource(data);
  };

  useEffect(() => {
    (async () => {
      const params = props.data;
      if (params) {
        const result = await initApplyPageContext(params);
        if (result.code !== ResponseStatus.SUCCESS) {
          message.error(result.message);
          return;
        }
        let res = await Staff.current();
        if (res.data) {
          formRef.current?.setFieldsValue({
            projectApproverIds: {value: res.data.staffId, label: res.data.staffName},
          });
        }
        const viewEntity = result.data;
        let limitedEcIds, limitedProductIds, limitedCustSysCodes, limitedSoldToCodes, projectApproverIds,
          limitedActivitySubjectIds;
        if (viewEntity.budgetProject) {
          formRef.current?.setFieldsValue({
            projectName: viewEntity.budgetProject.projectName,
            projectDate: [
              moment(viewEntity.budgetProject.projectStartDate),
              moment(viewEntity.budgetProject.projectEndDate),
            ],
            projectRemark: viewEntity.budgetProject.projectRemark,
            fileInfo: FileUploadUtility.parse(
              viewEntity.budgetProject.fileName,
              viewEntity.budgetProject.filePath,
            ),
            applyDate: [
              moment(viewEntity.budgetProject.applyStartDate),
              moment(viewEntity.budgetProject.applyEndDate),
            ],
            modifiedReason: viewEntity.budgetProject.modifiedReason,
          });
          limitedEcIds = viewEntity.budgetProject.limitedEcIds;
          limitedProductIds = viewEntity.budgetProject.limitedProductIds;
          limitedCustSysCodes = viewEntity.budgetProject.limitedCustSysCodes;
          limitedSoldToCodes = viewEntity.budgetProject.limitedSoldToCodes;
          projectApproverIds = viewEntity.budgetProject.projectApproverIds;
          limitedActivitySubjectIds = viewEntity.budgetProject.limitedActivitySubjectIds;
        } else if (viewEntity.formBudgetProject) {
          formRef.current?.setFieldsValue({
            projectName: viewEntity.formBudgetProject.projectName,
            projectDate: [
              moment(viewEntity.formBudgetProject.projectStartDate),
              moment(viewEntity.formBudgetProject.projectEndDate),
            ],
            projectRemark: viewEntity.formBudgetProject.projectRemark,
            fileInfo: FileUploadUtility.parse(
              viewEntity.formBudgetProject.fileName,
              viewEntity.formBudgetProject.filePath,
            ),
            applyDate: [
              moment(viewEntity.formBudgetProject.applyStartDate),
              moment(viewEntity.formBudgetProject.applyEndDate),
            ],
            modifiedReason: viewEntity.formBudgetProject.modifiedReason,
          });
          limitedEcIds = viewEntity.formBudgetProject.limitedEcIds;
          limitedProductIds = viewEntity.formBudgetProject.limitedProductIds;
          limitedCustSysCodes = viewEntity.formBudgetProject.limitedCustSysCodes;
          limitedSoldToCodes = viewEntity.formBudgetProject.limitedSoldToCodes;
          projectApproverIds = viewEntity.formBudgetProject.projectApproverIds;
          limitedActivitySubjectIds = viewEntity.formBudgetProject.limitedActivitySubjectIds;

        }

        if (limitedEcIds) {
          EC.search({ecIds: limitedEcIds, isActive: true}).then((response) => {
            if (response.data && response.data.length > 0) {
              setExpandKeysEC(response.data.map((ec) => ec.ecId.toString()));
              formRef.current?.setFieldsValue({
                limitedEcIds: response.data.map((ec) => ({value: ec.ecId, label: ec.ecName})),
              });
            }
          });
        }

        if (limitedProductIds) {
          Product.searchAll({productIds: limitedProductIds, isActive: true}).then((response) => {
            if (response.data && response.data.length > 0) {
              setExpandKeysProduct(response.data.map((product) => product.productId.toString()));
              formRef.current?.setFieldsValue({
                limitedProductIds: response.data.map((product) => ({
                  value: product.productId,
                  label: product.codeOrName,
                })),
              });
            }
          });
        }
        if (limitedCustSysCodes) {
          CustSys.search({custSysCodes: limitedCustSysCodes, isActive: true, withPermissions: true}).then(res => {
            if (res.data && res.data.length > 0) {
              formRef.current?.setFieldsValue({
                limitedCustSysCodes: res.data.map(custSys => ({value: custSys.custSysCode, label: custSys.codeOrName}))
              });
              setCustSysCodes(res.data.map(custSys => custSys.custSysCode).join(','));
            }
          })
        }
        if (limitedSoldToCodes) {
          SoldTo.search({soldToCodes: limitedSoldToCodes, isActive: true, withPermissions: true}).then(response => {
            if (response.data && response.data.length > 0) {
              formRef.current?.setFieldsValue({
                limitedSoldToCodes: response.data.map(soldTo => ({value: soldTo.soldToCode, label: soldTo.codeOrName}))
              });
            }
          });
        }
        if (projectApproverIds) {
          Staff.search({staffIds: projectApproverIds, isActive: true,}).then(res => {
            if (res.data && res.data.length > 0) {
              formRef.current?.setFieldsValue({
                projectApproverIds: res.data.map(staff => ({value: staff.staffId, label: staff.staffName}))
              });
            }
          })
        }
        if (limitedActivitySubjectIds) {
          DataOptions.search({typeGroup: 121, inValues: limitedActivitySubjectIds}).then(res => {
            if (res.data && res.data.length > 0) {
              formRef.current?.setFieldsValue({
                limitedActivitySubjectIds: res.data.map(item => ({value: item.value, label: item.name}))
              });
            }
          })
        }
        setEntity(viewEntity);

        if (viewEntity.bohqId) {
          BOHQ.get(viewEntity.bohqId).then((response) => {
            setBOHQ(response.data);
            formRef.current?.setFieldsValue({
              bohqId: response.data.bohqId,
              bohqName: response.data.bohqName,
            });
          });
        }

        if (viewEntity.pageTypeId) {
          PageType.get(viewEntity.pageTypeId).then((response) => {
            setPageType(response.data);
            formRef.current?.setFieldsValue({
              pageTypeId: response?.data.pageTypeId,
            });
          });
        }

        if (viewEntity.budgetProject) {
          BudgetProjectDetail.query(
            viewEntity.budgetProject.budgetProjectId,
            viewEntity.budgetProject.buId,
          ).then((response) => {
            setDataSource(DataStore.init(response.data));
          });
        } else if (viewEntity.formBudgetProject) {
          FormBudgetProjectDetail.query(
            viewEntity.formBudgetProject.formBudgetProjectId,
            viewEntity.formBudgetProject.buId,
          ).then((response) => {
            setDataSource(DataStore.init(response.data));
          });
        } else {
          setDataSource(DataStore.init([]));
        }

        if (
          viewEntity.budgetProject ||
          (viewEntity.formBudgetProject &&
            viewEntity.relationType === Enum.FormRelationType.REJECTED)
        ) {
          setHiddenButtons(hiddenButton.concat(ApplyFooterType.Draft));
        }
      }
    })();
  }, [props.data]);

  useEffect(() => {
    if (props.setPageTitle && pageType && entity) {
      let title = '专项项目申请';
      if (entity.budgetProject) {
        title += '【编辑】';
      }
      props.setPageTitle(title);
    }
  }, [props.setPageTitle, pageType, entity]);

  return (
    <>
      <BPWarningInfoApp {...warningSettings} />
      <ProForm
        formRef={formRef}
        submitter={{
          render: () => {
            return (
              <ApplyFooter
                setLoading={props.setLoading}
                hiddenButton={hiddenButton}
                viewEntity={entity}
                submitBackUrl="/project/budgetproject"
                doSubmit={async (type: number, forceSubmit: boolean) => {
                  if (!entity || !pageType || !dataSource) {
                    return;
                  }
                  const remark = await editorRef?.current?.getHtml?.();
                  const values = await formRef.current?.validateFieldsReturnFormatValue?.();
                  if (values && values.errorFields && values.errorFields.length) {
                    return;
                  }
                  const statusId =
                    type === ApplyFooterType.Draft
                      ? Enum.FormStatus.DRAFT
                      : Enum.FormStatus.AWAITING;
                  values.projectRemark = remark;
                  const req = getPageResult(values, entity, pageType, statusId, dataSource);

                  if (req.code !== ResponseStatus.SUCCESS) {
                    message.error(req.message);
                    return;
                  }
                  props?.setLoading?.(true);
                  if (type === ApplyFooterType.Validate) {
                    return await FormBudgetProject.Buttons.validate(req.data);
                  } else {
                    return await FormBudgetProject.Buttons.apply({
                      forceSubmit: forceSubmit,
                      ...req.data,
                    });
                  }
                }}
                del={async () => {
                  if (!entity || !entity.formId) {
                    return;
                  }
                  return await FormBudgetProject.Buttons.del(entity.formId);
                }}
                showWarning={(result: WdiModalAppResult, submitCallback: any) => {
                  setWarningSettings({
                    visible: true,
                    title: '警示信息',
                    wdiPanelList: result.data,
                    onClose: () => {
                      setWarningSettings({visible: false});
                    },
                    onSave: async () => {
                      await submitCallback();
                    },
                  });
                }}
              />
            );
          },
        }}
      >
        <WdiPanel title={<WdiCardTitle iconType={Enum.IconType.BASE_INFO} title="项目基本信息"/>}>
          <Row gutter={[16, 24]}>
            <Col className="gutter-row" span={8}>
              <Form.Item
                name="projectName"
                label="项目名称"
                rules={[{required: true, message: '项目名称不能为空!'}]}
              >
                <Input/>
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={8}>
              <Form.Item
                required
                name="projectDate"
                label="活动实际执行期间"
                rules={[{required: true, message: '活动实际执行期间限制不能为空!'}]}
              >
                <RangePicker/>
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item name="projectApproverIds" label="活动申请/核销附加审批人">
                <WdiSelect
                  {...{
                    placeholder: '请选择活动申请/核销附加审批人',
                    showSearch: true,
                    labelInValue: true,
                    remote: true,
                    dataLoading: async (params) => {
                      const response = await Staff.ajaxSearch(Object.assign({isActive: true}, params));
                      return response.data.map((item) => {
                        return {
                          value: item.staffId,
                          label: wdiCore.String.GenerateLabelDescription(
                            item.staffNoOrName,
                            item.title,
                          ),
                        };
                      });
                    },
                    mode: 'multiple',
                    defaultParamKey: "staffIds",

                  }}
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item
                required
                name="bohqName"
                label="预算归属"
                rules={[{required: true, message: '预算归属不能为空!'}]}
              >
                <WdiInput readOnly/>
              </Form.Item>
              <Form.Item name="bohqId" label="bohqId" hidden>
                <WdiInput readOnly/>
              </Form.Item>
            </Col>
            {/*<Col className="gutter-row" span={4}>*/}
            <Form.Item name="pageTypeId" label="pageTypeId" hidden>
              <Input readOnly/>
            </Form.Item>
            {/*</Col>*/}

          </Row>
          <Row gutter={[16, 24]}>
            <Col className="gutter-row" span={16}>
              <Form.Item
                name="projectRemark"
                label="方案简述"
                rules={[{required: true, message: '方案简述不能为空!'}]}
              >
                <WdiEditor setRef={setEditorRef}/>
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={8}>
              <Form.Item
                name="fileInfo"
                label="附件"
                valuePropName="fileList"
                getValueFromEvent={(e: any) => {
                  return e.fileList || [];
                }}
              >
                <WdiFileUpload leftTopInfo="请选择文件并上传(单个文件不能超500M)"/>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 24]}>
            <Col className="gutter-row" span={16} hidden={!entity?.budgetProject}>
              <Form.Item
                name="modifiedReason"
                label="修改原因"
                rules={[
                  {required: !!entity?.budgetProject, message: '修改原因不能为空!'},
                ]}
              >
                <TextArea rows={4}/>
              </Form.Item>
            </Col>
          </Row>
        </WdiPanel>
        <WdiPanel
          title={<WdiCardTitle iconType={Enum.IconType.LIMITED_INFO} title="维度信息（对专项下的活动申请做限制）"/>}>
          <Row gutter={[16, 24]}>
            <Col className="gutter-row" span={6}>
              <Form.Item
                required
                name="applyDate"
                label="专项费用可申请时间"
                rules={[{required: true}]}
              >
                <RangePicker/>
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4} hidden={true}>
              <Form.Item name="limitedProductIds" label="产品别维度">
                <WdiTreeSelect
                  {...{
                    placeholder: '请选择产品别',
                    multiple: true,
                    defaultExpandKeys: expandKeysProduct,
                    dataLoading: async (params) => {
                      const response = await Product.searchNoRoot(
                        Object.assign(
                          {
                            productType: 1,
                            isActive: true,
                            withPermissions: true
                          },
                          params,
                        ),
                      );
                      const {treeData, dict} = FieldProps.TreeSelect.formatTreeData(
                        {
                          textField: 'productName',
                          valueField: 'productId',
                          id: 'productId',
                          pId: 'parentId',
                        },
                        response.data,
                      );
                      return {treeData, dict};
                    },
                    onExSelect: (value: any, node: any, dict: any) => {
                      const values = formRef.current?.getFieldValue('limitedProductIds');
                      const rtv = FieldProps.TreeSelect.labeledValuesMeger(dict, values, {value});
                      formRef.current?.setFieldsValue({limitedProductIds: rtv});
                    },
                  }}
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4} hidden={true}>
              <Form.Item name="limitedEcIds" label="费用项维度">
                <WdiTreeSelect
                  {...{
                    placeholder: '请选择费用项',
                    multiple: true,
                    defaultExpandKeys: expandKeysEC,
                    dataLoading: async (params) => {
                      const response = await EC.searchEc3(
                        Object.assign(
                          {
                            bohqId: entity?.bohqId,
                            toParentLevel: String(Enum.ECLevel.EC1),
                            isActive: true,
                          },
                          params,
                        ),
                      );
                      const {treeData, dict} = FieldProps.TreeSelect.formatTreeData(
                        {
                          textField: 'ecName',
                          valueField: 'ecId',
                          id: 'ecId',
                          pId: 'parentId',
                          level: 'ecLevel',
                          disabledLevel: [Enum.ECLevel.ROOT, Enum.ECLevel.EC1, Enum.ECLevel.EC2],
                        },
                        response.data,
                      );
                      return {treeData, dict};
                    },
                    reloadKey: entity?.bohqId,
                  }}
                />
              </Form.Item>
            </Col>

            <Col className="gutter-row" span={4}>
              <Form.Item name="limitedCustSysCodes" label="客户组维度">
                <WdiSelect
                  {...{
                    mode: 'multiple',
                    placeholder: '请选择客户组维度',
                    showSearch: true,
                    remote: true,
                    labelInValue: true,
                    dataLoading: async (params) => {
                      const responseResult = await CustSys.ajaxSearch(
                        Object.assign(
                          {
                            isActive: true,
                            withPermissions: true,
                          },
                          params,
                        ),
                      );
                      return responseResult.data?.map((item) => {
                        return {value: item.custSysCode, label: item.codeOrName};
                      });
                    },
                    onChange: (params) => {
                      formRef.current?.setFieldsValue({
                        limitedSoldToCodes: []
                      })
                      setCustSysCodes(WdiSelectFormat.getvalue(params))
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item name="limitedSoldToCodes" label="客户维度">
                <WdiSelect
                  {...{
                    mode: "multiple",
                    placeholder: "请选择客户",
                    showSearch: true,
                    remote: true,
                    reloadKey: custSysCodes,
                    labelInValue: true,
                    dataLoading: async (params) => {
                      const responseResult = await SoldTo.ajaxSearch(Object.assign({
                        isActive: true,
                        withPermissions: true,
                        custSysCodes: custSysCodes
                      }, params));
                      return responseResult.data?.map((item) => {
                        return {value: item.soldToCode, label: item.codeOrName};
                      });
                    }
                  }} />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item name="limitedActivitySubjectIds" label="活动主题维度">
                <WdiSelect {...{
                  placeholder: "请选择活动主题",
                  mode: "multiple",
                  dataLoading: async (params) => {
                    const response = await DataOptions.search(Object.assign({typeGroup: 121, isActive: true}, params));
                    return response.data.map((item) => ({value: item.value, label: item.name}));
                  }
                }} />
              </Form.Item>
            </Col>
          </Row>
        </WdiPanel>
        <FormBPDetailsForApplyPanel
          data={{
            pageTypeId: pageType?.pageTypeId,
            bohqId: boHQ?.bohqId,
            budgetProject: entity?.budgetProject,
          }}
          dataSource={dataSource}
          onChange={onDetailsChange}
        />
      </ProForm>
    </>
  );
};
export default FormBudgetProjectApplyApp;
